import { useEffect, useMemo, useState } from 'react';
import useSlideshows, { ActivationTag } from './hooks/useSlideshows';

export interface GalleryTagFilterValue {
    enable: boolean;
    mode: 'include' | 'exclude';
    tags: string[];
    matchMode: 'any' | 'all';
}

interface GalleryTagFilterSettingsProps {
    /** Current gallery tag filter; null when the gallery has never had one. */
    value: GalleryTagFilterValue | null | undefined;
    onChange: (value: GalleryTagFilterValue) => void;
    activationId: number;
}

const emptyFilter: GalleryTagFilterValue = {
    enable: false,
    mode: 'include',
    tags: [],
    matchMode: 'any',
};

// Mirrors cloud/modules/mediaTags/service normalizeTag so chips shown here
// match what the server will store.
function normalizeTag(tag: string): string {
    return tag.trim().replace(/\s+/g, ' ').toLowerCase();
}

/**
 * Gallery-level tag filter (gallery_tag_filter): show only tagged media, or
 * hide tagged media, everywhere the gallery's photos appear. Composes with
 * the media type filter above it and with tag-scoped slideshows. This is the
 * classic-dashboard twin of the v2 Content section in GalleryV2.
 */
export default function GalleryTagFilterSettings({
    value,
    onChange,
    activationId,
}: GalleryTagFilterSettingsProps) {
    const { getActivationTags } = useSlideshows();
    const [knownTags, setKnownTags] = useState<ActivationTag[]>([]);
    const [tagInput, setTagInput] = useState('');

    const filter = value ?? emptyFilter;

    useEffect(() => {
        let isMounted = true;

        getActivationTags(activationId)
            .then((tags) => {
                if (isMounted) {
                    setKnownTags(tags);
                }
            })
            .catch((e) => console.error(e));

        return () => {
            isMounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activationId]);

    const suggestedTags = useMemo(
        () => knownTags.filter(({ tag }) => !filter.tags.includes(tag)),
        [knownTags, filter.tags],
    );

    const addTag = (rawTag: string) => {
        const tag = normalizeTag(rawTag);

        if (!tag || filter.tags.includes(tag)) {
            return;
        }

        onChange({ ...filter, tags: [...filter.tags, tag] });
        setTagInput('');
    };

    const removeTag = (tag: string) => {
        onChange({ ...filter, tags: filter.tags.filter((t) => t !== tag) });
    };

    return (
        <div className="space-y-4">
            <div>
                <h4 className="text-md font-medium text-gray-900 dark:text-white">
                    Tag Filter
                </h4>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    Show only tagged media, or hide tagged media, everywhere
                    this gallery&apos;s photos appear — the web gallery,
                    slideshows, and shared links. Applies on top of the media
                    type filters above.
                </p>
            </div>

            <div className="flex items-center space-x-2">
                <input
                    id="galleryTagFilterEnable"
                    type="checkbox"
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    checked={filter.enable}
                    onChange={(e) =>
                        onChange({ ...filter, enable: e.target.checked })
                    }
                />
                <label
                    htmlFor="galleryTagFilterEnable"
                    className="text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                    Enable tag filter
                </label>
            </div>

            {filter.enable && (
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-1">
                            Mode
                        </label>
                        <select
                            className="rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
                            value={filter.mode}
                            onChange={(e) =>
                                onChange({
                                    ...filter,
                                    mode: e.target.value as
                                        | 'include'
                                        | 'exclude',
                                })
                            }
                        >
                            <option value="include">
                                Only show media with these tags
                            </option>
                            <option value="exclude">
                                Hide media with these tags
                            </option>
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-1">
                            Tags
                        </label>
                        <div className="flex flex-wrap items-center gap-1 mb-2">
                            {filter.tags.map((tag) => (
                                <span
                                    key={tag}
                                    className="inline-flex items-center gap-1 rounded-full bg-gray-100 dark:bg-gray-700 px-2 py-0.5 text-xs text-gray-700 dark:text-gray-200"
                                >
                                    {tag}
                                    <button
                                        type="button"
                                        className="hover:text-red-500"
                                        onClick={() => removeTag(tag)}
                                    >
                                        ×
                                    </button>
                                </span>
                            ))}
                            {filter.tags.length === 0 && (
                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                    No tags yet — the filter has no effect
                                    until a tag is added
                                </span>
                            )}
                        </div>
                        <input
                            type="text"
                            className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
                            placeholder="Type a tag and press Enter"
                            value={tagInput}
                            onChange={(e) => setTagInput(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    addTag(tagInput);
                                }
                            }}
                        />
                        {suggestedTags.length > 0 && (
                            <div className="mt-2 flex flex-wrap items-center gap-1">
                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                    In use:
                                </span>
                                {suggestedTags.map(({ tag, count }) => (
                                    <button
                                        key={tag}
                                        type="button"
                                        className="inline-flex items-center rounded-full border border-gray-300 dark:border-gray-600 px-2 py-0.5 text-xs text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                                        onClick={() => addTag(tag)}
                                    >
                                        + {tag}
                                        <span className="ml-1 text-gray-400">
                                            {count}
                                        </span>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    {filter.tags.length > 1 && (
                        <div>
                            <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-1">
                                Match
                            </label>
                            <select
                                className="rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
                                value={filter.matchMode}
                                onChange={(e) =>
                                    onChange({
                                        ...filter,
                                        matchMode: e.target.value as
                                            | 'any'
                                            | 'all',
                                    })
                                }
                            >
                                <option value="any">
                                    Any tag (photo has at least one)
                                </option>
                                <option value="all">
                                    All tags (photo has every one)
                                </option>
                            </select>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
